import * as React from "react";
import { KeyValue, Query } from "../../../interfaces";
import { TableCell, Chip, Checkbox, TableRow } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SODAddressList from "./SODAddressList";
import generateBranchOfficeDepositsApiUrl from "./BranchOfficeDepositsUtilities";
import { fetchData } from "../../../common/services/ApiClient";
import { ApiBaseUrl } from "../../../config";
import useOpenCloseAll from "../../../hooks/useOpenCloseAll";
import SODInstituteList from "./SODInstituteList";
import { chunkArray, reduceObj } from "../../../common/services/Utilities";

interface Props {
  stateIndex: number;
  appState: Query;
  stateName: string;
  openCloseAllDisplay: KeyValue;
  setOpenCloseAllDisplay: (openCloseAllDisplay: KeyValue) => void;
  openClose: string;
  setOpenClose: (openClose: string) => void;
  cert?: string;
  fromBHC?: boolean;
  locationOnlySearch?: boolean;
  checked?: KeyValue;
  setChecked?: (checked: KeyValue) => void;
}

export default function SODCountyList({
  stateIndex,
  appState,
  stateName,
  openCloseAllDisplay,
  setOpenCloseAllDisplay,
  openClose,
  setOpenClose,
  cert,
  fromBHC,
  locationOnlySearch,
  checked,
  setChecked,
}: Props) {
  const [open, setOpen] = React.useState<string[]>(
    appState.filters.county ? [appState.filters.county] : [],
  );
  const [countyData, setCountyData] = React.useState<KeyValue[]>([]);
  const [previousSearch, setPreviousSearch] = React.useState<KeyValue>({});
  const [locationOnly, setLocationOnly] = React.useState(locationOnlySearch);
  const [initialLoad, setInitialLoad] = React.useState(true);
  const isBHC = appState.ui.institutionType === "bhcs";
  let bhcFilter = "";
  if (locationOnly && isBHC) {
    bhcFilter = "RSSDHCR:>0";
  }
  React.useEffect(() => {
    if (
      checked &&
      open.length > 0 &&
      open.every(
        (key) =>
          checked[`${stateName}_${key}`] &&
          checked[`${stateName}_${key}`].count > 0,
      )
    ) {
      open.forEach((key) => {
        checked[`${stateName}_${key}`].count = 0;
        checked[`${stateName}_${key}`].deposits = 0;
      });
    }
  }, [checked, open, stateName]);
  React.useEffect(() => {
    if (initialLoad && setChecked && checked && countyData.length > 0) {
      setInitialLoad(false);
      const allCountiesChecked = reduceObj(
        countyData.map((county: KeyValue) => {
          const subDataIncluded = Object.keys(checked).some((key) =>
            key.includes(`${stateName}_${county.CNTYNAMB}_`),
          );
          return {
            key: `${stateName}_${county.CNTYNAMB}`,
            value:
              checked[`${stateName}_${county.CNTYNAMB}`] !== undefined
                ? {
                    checked:
                      checked[`${stateName}_${county.CNTYNAMB}`]?.checked,
                    count: subDataIncluded ? 0 : county.count,
                    deposits: subDataIncluded ? 0 : county.sum_DEPSUMBR,
                  }
                : {
                    checked: checked[stateName]?.checked,
                    count: subDataIncluded ? 0 : county.count,
                    deposits: subDataIncluded ? 0 : county.sum_DEPSUMBR,
                  },
          };
        }),
      );
      setChecked((prev: KeyValue) => ({
        ...prev,
        ...allCountiesChecked,
      }));
    }
  }, [checked, countyData, initialLoad, setChecked, stateName]);

  useOpenCloseAll({
    data: countyData,
    key: "CNTYNAMB",
    currentValues: open,
    openClose: openClose === stateName,
    parent: stateName,
    setValues: setOpen,
    setParent: setOpenClose,
    buttonDisplays: openCloseAllDisplay,
    setButtonDisplays: setOpenCloseAllDisplay,
  });
  React.useEffect(() => {
    const filtersSearch = {
      filters: appState.filters,
      search: appState.search,
      sort: appState.sort,
      marketSelect: appState.ui.marketSelection,
    };
    const bhcCert = cert ? { cert } : {};
    if (
      (appState.filters.cert ||
        cert ||
        appState.filters.OTSDocket ||
        appState.filters.rssdid ||
        locationOnly) &&
      Object.keys(appState.ui.marketSelection[0]).length > 0 &&
      stateName &&
      JSON.stringify(filtersSearch) !== JSON.stringify(previousSearch)
    ) {
      locationOnly && setLocationOnly(false);
      const fetchUrls = appState.filters.stateNames.map(
        (state: string, index: number) => {
          const marketSelecion = appState.ui.marketSelection[index];
          if (marketSelecion?.zips) {
            const zips = chunkArray(marketSelecion?.zips, 500);
            return zips.map((zipChunk) =>
              generateBranchOfficeDepositsApiUrl(
                {
                  ...appState,
                  filters: {
                    ...appState.filters,
                    stalpbr: stateName,
                    ...bhcCert,
                  },
                },
                {
                  by: "CNTYNAMB",
                  limit: 10000,
                  sum: ["DEPSUMBR"],
                },
                state,
                { ...marketSelecion, zips: zipChunk },
                locationOnlySearch && isBHC
                  ? "RSSDHCR"
                  : locationOnlySearch
                    ? "CERT"
                    : undefined,
                bhcFilter,
              ),
            );
          } else {
            return generateBranchOfficeDepositsApiUrl(
              {
                ...appState,
                filters: {
                  ...appState.filters,
                  stalpbr: stateName,
                  ...bhcCert,
                },
              },
              {
                by: "CNTYNAMB",
                limit: 10000,
                sum: ["DEPSUMBR"],
              },
              state,
              marketSelecion,
              locationOnlySearch && isBHC
                ? "RSSDHCR"
                : locationOnlySearch
                  ? "CERT"
                  : undefined,
              bhcFilter,
            );
          }
        },
      );
      setPreviousSearch(filtersSearch);
      let isMounted = true;
      (async () => {
        await Promise.all(
          fetchUrls
            .flat()
            .map((url: string) => fetchData(`${ApiBaseUrl()}/sod`, url)),
        )
          .then((response) => {
            if (isMounted) {
              const data = response
                .map((res: any) => res.data)
                .filter((res) => res[0])[0];
              setCountyData(data);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => (isMounted = false));
      })();
    }
  }, [
    appState,
    cert,
    previousSearch,
    stateName,
    locationOnly,
    locationOnlySearch,
    isBHC,
    bhcFilter,
  ]);
  React.useEffect(() => {
    const filtersSearch = {
      filters: appState.filters,
      search: appState.search,
      sort: appState.sort,
    };

    const bhcCert = cert ? { cert } : {};
    if (
      (appState.filters.msa ||
        appState.filters.zipbr ||
        appState.filters.cert ||
        cert ||
        appState.filters.OTSDocket ||
        appState.filters.rssdid) &&
      Object.keys(appState.ui.marketSelection[0]).length === 0 &&
      JSON.stringify(filtersSearch) !== JSON.stringify(previousSearch) &&
      stateName
    ) {
      setPreviousSearch(filtersSearch);
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod`,
          generateBranchOfficeDepositsApiUrl(
            {
              ...appState,
              filters: { ...appState.filters, stalpbr: stateName, ...bhcCert },
            },
            {
              by: "CNTYNAMB",
              limit: 10000,
              sum: ["DEPSUMBR"],
            },
            undefined,
            undefined,
            isBHC ? "RSSDHCR" : "CERT",
            bhcFilter,
          ),
        )
          .then((response) => {
            if (isMounted) {
              setCountyData(response.data);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => (isMounted = false));
      })();
    }
  }, [appState, bhcFilter, cert, isBHC, openClose, previousSearch, stateName]);

  const locationOnlyTableColumns = (data: KeyValue, expandProps: KeyValue) => {
    return (
      <>
        <TableCell {...expandProps} />
        <TableCell {...expandProps} />
        <TableCell
          style={{ color: "#0000EE", textDecoration: "underline" }}
          {...expandProps}
        >
          {data.unique_count}
        </TableCell>
        <TableCell style={{ color: "#0000EE", textDecoration: "underline" }}>
          {data.count}
        </TableCell>
        <TableCell {...expandProps} />
        {!isBHC && <TableCell {...expandProps} />}
        <TableCell {...expandProps} style={{ textAlign: "right" }}>
          {data.sum_DEPSUMBR ? data.sum_DEPSUMBR.toLocaleString() : 0}
        </TableCell>
      </>
    );
  };

  const certTableColumns = (
    data: KeyValue,
    expandProps: KeyValue,
    checkBoxState?: boolean,
    indeterminateState?: boolean,
  ) => {
    return (
      <>
        <TableCell {...expandProps} />
        {appState.ui.partialMerger?.length > 0 &&
          appState.filters.cert !== appState.ui.certAcquiring &&
          checked &&
          setChecked &&
          checkBoxState !== undefined && (
            <TableCell>
              <Checkbox
                id={`${stateName}_${data.CNTYNAMB}_checkbox`}
                checked={!!checkBoxState}
                sx={{
                  "&:hover": {
                    backgroundColor: "#FFFFFF",
                  },
                }}
                inputProps={{ "aria-label": "checkbox" }}
                onChange={() => {
                  const allChecks: any = Object.keys(checked)
                    .map((val) =>
                      val.startsWith(`${stateName}_${data.CNTYNAMB}_`) ||
                      val === `${stateName}_${data.CNTYNAMB}`
                        ? {
                            key: val,
                            value: indeterminateState
                              ? {
                                  ...checked[val],
                                  checked: false,
                                }
                              : {
                                  ...checked[val],
                                  checked:
                                    !checked[`${stateName}_${data.CNTYNAMB}`]
                                      ?.checked,
                                },
                          }
                        : undefined,
                    )
                    .filter((data) => data);

                  const newState = {
                    ...checked,
                    [`${stateName}_${data.CNTYNAMB}`]: {
                      ...checked[`${stateName}_${data.CNTYNAMB}`],
                      checked: !checkBoxState,
                    },
                  };
                  const allCheckState = Object.keys(newState)
                    .map((check) =>
                      check.includes(`${stateName}_`)
                        ? newState[check].checked
                        : "",
                    )
                    .filter((data) => data !== "");

                  const parentState = {
                    [stateName]: {
                      ...checked[stateName],
                      checked: allCheckState.every((check) => check),
                    },
                  };
                  setChecked({
                    ...checked,
                    ...parentState,
                    ...reduceObj(allChecks),
                  });
                }}
                indeterminate={indeterminateState}
              />
            </TableCell>
          )}
        <TableCell {...expandProps} />
        <TableCell {...expandProps} />
        <TableCell
          {...expandProps}
          style={{ textDecoration: "underline", color: "#0000EE" }}
        >
          {data.count}
        </TableCell>
        <TableCell {...expandProps} />
        <TableCell {...expandProps} />
        <TableCell {...expandProps} />
        <TableCell {...expandProps} />
        <TableCell {...expandProps} style={{ textAlign: "right" }}>
          {appState?.ui?.pgcStep === "step1"
            ? ""
            : data.sum_DEPSUMBR
              ? data.sum_DEPSUMBR.toLocaleString()
              : 0}
        </TableCell>
      </>
    );
  };
  const bhcsTableColumns = (data: KeyValue, expandProps: KeyValue) => {
    return (
      <>
        <TableCell {...expandProps} />
        <TableCell {...expandProps} />
        <TableCell {...expandProps} />
        <TableCell {...expandProps} />
        <TableCell {...expandProps}>{data.count}</TableCell>
        <TableCell {...expandProps} />
        <TableCell {...expandProps} />
        <TableCell {...expandProps} />
        <TableCell {...expandProps} />
        <TableCell {...expandProps} />
        <TableCell {...expandProps} style={{ textAlign: "right" }}>
          {data.sum_DEPSUMBR ? data.sum_DEPSUMBR.toLocaleString() : 0}
        </TableCell>
      </>
    );
  };
  const countyList = countyData ? (
    countyData.map((data: KeyValue, int: number) => {
      const expandCollapse = (e: any) => {
        const otherClicks = ["Open All", "Close All"];
        if (!otherClicks.includes(e.target.innerText)) {
          setOpenCloseAllDisplay({
            ...openCloseAllDisplay,
            [stateName]: {
              ...openCloseAllDisplay[stateName],
              [data.CNTYNAMB]: !open.includes(data.CNTYNAMB) ? true : false,
            },
          });
          !open.includes(data.CNTYNAMB)
            ? setOpen([...open, data.CNTYNAMB])
            : setOpen(open.filter((county) => county !== data.CNTYNAMB));
          if (
            checked &&
            setChecked &&
            checked[`${stateName}_${data.CNTYNAMB}`]
          ) {
            if (!open.includes(data.CNTYNAMB)) {
              setChecked({
                ...checked,
                [`${stateName}_${data.CNTYNAMB}`]: {
                  checked: checked[`${stateName}_${data.CNTYNAMB}`]?.checked,
                  count: 0,
                  deposits: 0,
                },
              });
            }
          }
        }
      };
      const expandCollapseProps = {
        onKeyDown: (e: any) => {
          if (e.key === "Enter") {
            expandCollapse(e);
          }
        },
        onClick: (e: any) => expandCollapse(e),
      };
      const subCheckBoxes =
        checked &&
        Object.keys(checked)
          .map((check) =>
            check.includes(`${stateName}_${data.CNTYNAMB}_`)
              ? check
              : undefined,
          )
          .filter((data) => data);

      const indeterminateState =
        subCheckBoxes &&
        subCheckBoxes.some((subData: any) => checked[subData].checked) &&
        subCheckBoxes.some((subData: any) => !checked[subData].checked);

      const checkboxState =
        checked && subCheckBoxes?.length
          ? subCheckBoxes.every((subData: any) => checked[subData].checked)
          : checked && checked[`${stateName}_${data.CNTYNAMB}`]?.checked;

      return (
        <React.Fragment key={`${data.CNTYNAMB}_${int}`}>
          <TableRow
            id={`${data.CNTYNAMB?.toLowerCase().replace(
              / /g,
              "",
            )}_expand_${int}`}
            sx={(theme) => ({
              "& td.MuiTableCell-root.MuiTableCell-body": {
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.primary.main,
                fontWeight: "bold",
              },
              "&:hover td.MuiTableCell-root.MuiTableCell-body, &:focus-visible td.MuiTableCell-root.MuiTableCell-body":
                {
                  cursor: "pointer",
                  filter: "brightness(.93)",
                },
            })}
            tabIndex={0}
          >
            <TableCell
              sx={{
                "&.MuiTableCell-root.MuiTableCell-body": {
                  textAlign: "left",
                  paddingLeft: fromBHC ? "52px" : "30px",
                },
              }}
              {...expandCollapseProps}
            >
              <Chip
                sx={(theme) => ({
                  "cursor": "pointer",
                  "backgroundColor": "transparent",
                  "color": "#0000EE",
                  "textDecoration": "underline",
                  "fontSize": 14,
                  "float": "left",
                  "& .MuiChip-icon": {
                    color: theme.palette.primary.main,
                  },
                })}
                icon={
                  open.includes(data.CNTYNAMB) ? (
                    <ExpandMoreIcon />
                  ) : (
                    <ChevronRightIcon />
                  )
                }
                label={data.CNTYNAMB}
              />
            </TableCell>
            {locationOnlySearch
              ? locationOnlyTableColumns(data, expandCollapseProps)
              : !fromBHC
                ? certTableColumns(
                    data,
                    expandCollapseProps,
                    checkboxState,
                    indeterminateState,
                  )
                : bhcsTableColumns(data, expandCollapseProps)}
          </TableRow>
          {open.includes(data.CNTYNAMB) &&
            (locationOnlySearch ? (
              <SODInstituteList
                appState={appState}
                countyName={data.CNTYNAMB}
                stateName={stateName}
                stateIndex={stateIndex}
                isBHC={isBHC}
              />
            ) : (
              <SODAddressList
                countyName={data.CNTYNAMB}
                stateName={stateName}
                appState={appState}
                fromBHC={fromBHC}
                cert={cert}
                checked={checked}
                setChecked={setChecked}
              />
            ))}
        </React.Fragment>
      );
    })
  ) : (
    <></>
  );

  return <>{countyList}</>;
}
